import { useEffect, useId, useRef, useState, useSyncExternalStore, type ReactNode } from "react";

/**
 * In-app replacements for window.alert / confirm / prompt, plus toasts.
 *
 * Browser dialogs can be silently suppressed ("don't let this site prompt
 * again" makes confirm() return false without showing anything), which made
 * actions look like they did nothing. These are awaited the same way:
 *
 *   if (!(await confirmDialog({ title: "Delete comment?", danger: true, confirmLabel: "Delete" }))) return;
 *   await alertDialog({ title: "Couldn't save", message: err.message });
 *   const reason = await promptDialog({ title: "Why is this skipped?", required: true });
 *   notify("Invite re-sent.");
 *
 * <DialogHost /> is mounted once at the app root; dialogs queue, one at a time.
 */

type Base = { title?: string; message?: ReactNode };
export type ConfirmOptions = Base & { confirmLabel?: string; cancelLabel?: string; danger?: boolean };
export type AlertOptions = Base & { okLabel?: string };
export type PromptOptions = Base & {
  label?: string;
  defaultValue?: string;
  placeholder?: string;
  required?: boolean;
  multiline?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
};
export type ToastKind = "success" | "info" | "error";

type Request =
  | { id: number; kind: "confirm"; opts: ConfirmOptions; resolve: (v: boolean) => void }
  | { id: number; kind: "alert"; opts: AlertOptions; resolve: () => void }
  | { id: number; kind: "prompt"; opts: PromptOptions; resolve: (v: string | null) => void };
type Toast = { id: number; message: string; kind: ToastKind };

const TOAST_MS = 4000;
let nextId = 1;
let queue: Request[] = [];
let toasts: Toast[] = [];
let snapshot = { queue, toasts };
const listeners = new Set<() => void>();

function emit() {
  snapshot = { queue, toasts };
  listeners.forEach((l) => l());
}
function push(req: Request) {
  queue = [...queue, req];
  emit();
}
function settle(id: number) {
  queue = queue.filter((r) => r.id !== id);
  emit();
}

const asOpts = <T extends Base>(o: T | string): T => (typeof o === "string" ? ({ message: o } as T) : o);

export function confirmDialog(opts: ConfirmOptions | string): Promise<boolean> {
  return new Promise((resolve) => push({ id: nextId++, kind: "confirm", opts: asOpts(opts), resolve }));
}
export function alertDialog(opts: AlertOptions | string): Promise<void> {
  return new Promise((resolve) => push({ id: nextId++, kind: "alert", opts: asOpts(opts), resolve }));
}
export function promptDialog(opts: PromptOptions | string): Promise<string | null> {
  return new Promise((resolve) => push({ id: nextId++, kind: "prompt", opts: asOpts(opts), resolve }));
}

/** A short notice that fades on its own; for results, not decisions. */
export function notify(message: string, kind: ToastKind = "success"): void {
  const id = nextId++;
  toasts = [...toasts, { id, message, kind }];
  emit();
  setTimeout(() => {
    toasts = toasts.filter((t) => t.id !== id);
    emit();
  }, TOAST_MS);
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => { listeners.delete(l); };
}

export function DialogHost() {
  const state = useSyncExternalStore(subscribe, () => snapshot, () => snapshot);
  const current = state.queue[0];
  return (
    <>
      {current && <DialogView key={current.id} req={current} />}
      <div role="status" aria-live="polite" style={{
        position: "fixed", left: "50%", bottom: 24, transform: "translateX(-50%)", zIndex: 1200,
        display: "flex", flexDirection: "column", gap: 8, alignItems: "center", pointerEvents: "none",
      }}>
        {state.toasts.map((t) => (
          <div key={t.id} style={{
            padding: "10px 16px", borderRadius: 10, fontSize: 13, fontWeight: 500, boxShadow: "var(--shadow-lg)",
            background: t.kind === "error" ? "var(--tint-terra-soft)" : t.kind === "info" ? "var(--warm-card)" : "var(--tint-green-soft)",
            color: t.kind === "error" ? "var(--tint-terra-fg)" : t.kind === "info" ? "var(--warm-text)" : "var(--tint-green-fg)",
            border: "1px solid var(--warm-border)", pointerEvents: "auto",
          }}>
            {t.kind === "error" ? "✗ " : t.kind === "success" ? "✓ " : ""}{t.message}
          </div>
        ))}
      </div>
    </>
  );
}

function DialogView({ req }: { req: Request }) {
  const titleId = useId();
  const boxRef = useRef<HTMLDivElement>(null);
  const primaryRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  const opts = req.opts as PromptOptions & ConfirmOptions & AlertOptions;
  const [value, setValue] = useState(req.kind === "prompt" ? opts.defaultValue ?? "" : "");
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    (req.kind === "prompt" ? inputRef.current : primaryRef.current)?.focus();
    return () => { previous?.focus?.(); };
  }, [req.kind]);

  const done = (ok: boolean) => {
    if (req.kind === "prompt") {
      const text = value.trim();
      if (ok && opts.required && !text) {
        setMissing(true);
        inputRef.current?.focus();
        return;
      }
      settle(req.id);
      req.resolve(ok ? text : null);
    } else if (req.kind === "confirm") {
      settle(req.id);
      req.resolve(ok);
    } else {
      settle(req.id);
      req.resolve();
    }
  };

  // No key reaches the page while a dialog is open: Escape would close the
  // panel behind it, Ctrl+Enter would save a work order, Ctrl+K would open
  // the palette underneath. The box takes focus (tabIndex -1), so clicking
  // its text keeps keys here too.
  const onKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    const active = document.activeElement;
    if (e.key === "Escape") {
      e.preventDefault();
      done(false);
    } else if (e.key === "Enter") {
      if (active instanceof HTMLTextAreaElement) {
        // Plain Enter is a new line; Ctrl/Cmd+Enter submits.
        if (e.metaKey || e.ctrlKey) {
          e.preventDefault();
          done(true);
        }
        return;
      }
      e.preventDefault();
      if (active instanceof HTMLButtonElement && boxRef.current?.contains(active)) {
        // Act on the focused button: Enter on Cancel cancels, never confirms.
        active.click();
      } else if (active instanceof HTMLInputElement) {
        done(true);
      }
    } else if (e.key === "Tab") {
      // Keep focus inside the dialog.
      const focusable = Array.from(boxRef.current?.querySelectorAll<HTMLElement>("button, input, textarea") ?? []);
      const i = focusable.indexOf(document.activeElement as HTMLElement);
      const next = e.shiftKey ? (i <= 0 ? focusable.length - 1 : i - 1) : (i === focusable.length - 1 ? 0 : i + 1);
      e.preventDefault();
      focusable[next]?.focus();
    }
  };

  const primaryLabel = req.kind === "alert" ? opts.okLabel ?? "OK" : opts.confirmLabel ?? "OK";
  const title = opts.title ?? (req.kind === "confirm" ? "Are you sure?" : undefined);

  return (
    <>
      <div className="scrim" style={{ zIndex: 1100 }} onClick={() => done(false)} />
      <div ref={boxRef} className="modal" role="alertdialog" aria-modal="true" aria-labelledby={title ? titleId : undefined}
           tabIndex={-1}
           style={{ zIndex: 1101, width: 440, outline: "none" }} onKeyDown={onKeyDown}>
        {title && (
          <div className="modal-head">
            <h2 id={titleId} style={{ fontSize: 18, margin: 0 }}>{title}</h2>
          </div>
        )}
        <div className="modal-body" style={{ fontSize: 14, whiteSpace: "pre-wrap", paddingTop: title ? 4 : 20 }}>
          {opts.message}
          {req.kind === "prompt" && (
            <div className="field" style={{ marginTop: opts.message ? 12 : 0, marginBottom: 0 }}>
              {opts.label && <label>{opts.label}</label>}
              {opts.multiline ? (
                <textarea ref={inputRef} className="input" rows={3} value={value} placeholder={opts.placeholder}
                          onChange={(e) => { setValue(e.target.value); setMissing(false); }} />
              ) : (
                <input ref={inputRef} className="input" value={value} placeholder={opts.placeholder}
                       onChange={(e) => { setValue(e.target.value); setMissing(false); }} />
              )}
              {missing && <span style={{ color: "var(--terracotta)", fontSize: 11.5 }}>An answer is required.</span>}
            </div>
          )}
        </div>
        <div className="modal-foot">
          {req.kind !== "alert" && (
            <button className="btn btn-ghost" onClick={() => done(false)}>{opts.cancelLabel ?? "Cancel"}</button>
          )}
          <button ref={primaryRef} className={`btn ${opts.danger ? "btn-danger" : "btn-primary"}`} onClick={() => done(true)}>
            {primaryLabel}
          </button>
        </div>
      </div>
    </>
  );
}
